import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Box, Fade, Modal, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import type { TFunction } from 'i18next';
import React, { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, SemanticError } from '../../../../core-components/theme/color-palette/colors';
import { Button } from '../../../../core-components/theme/components/Buttons';
import { visuallyHiddenSx } from '../../../../core-components/theme/components/Fields/fieldStyles';
import { InlineAlert } from '../../../../core-components/theme/components/InlineAlert';
import { StickyActionBar } from '../../../../core-components/theme/components/StickyActionBar';
import { useAlarmTone } from '../../../../utilities/customHooks/useAlarmTone';
import type { AlarmTone } from '../../../../utilities/customHooks/useAlarmTone';
import { ALARM_PULSE_MS, IN_APP_CARD_MAX } from '../../constants/inApp';
import type { AlertPhase } from '../../hooks/logic/useInAppAlerts';
import { useLocale } from '../../hooks/logic/useLocale';
import type { FeedItem, IssueReading } from '../../types/domain.types';
import { formatReading } from '../../utils/format';
import { alertLines, emergencyEyebrow, firedLine, readingContext } from '../shared/alertLines';

import { alarmStatusLines, moreEmergenciesText, startFailedText } from './inAppText';
import { firedWhen } from './whenText';

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.7; }
`;

const phoneGutter = (theme: Theme) => ({
  paddingInlineStart: `max(env(safe-area-inset-left), ${theme.spacing(4)})`,
  paddingInlineEnd: `max(env(safe-area-inset-right), ${theme.spacing(4)})`,
});
const CARD_GUTTER_MD = 6;

const dockSx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...phoneGutter(theme),
  [theme.breakpoints.up('md')]: {
    paddingInline: theme.spacing(CARD_GUTTER_MD),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

const rootSx: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: { xs: 'stretch', md: 'center' },
  justifyContent: 'center',
  p: { xs: 0, md: 3 },
};

const backdropSx: SystemStyleObject<Theme> = {
  backgroundColor: alpha(SemanticError[900], 0.92),
  [REDUCED_MOTION]: { transition: 'none !important' },
};

const cardSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'grid',
  gridTemplateRows: 'auto minmax(0, 1fr) auto',
  inlineSize: '100%',
  blockSize: '100dvh',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 0,
  outline: 'none',
  [theme.breakpoints.up('md')]: {
    maxInlineSize: `${IN_APP_CARD_MAX}px`,
    blockSize: 'auto',
    maxBlockSize: `calc(100dvh - 2 * ${theme.spacing(3)})`,
    borderRadius: 2.5,
    boxShadow: theme.customShadows.dialog,
  },
  [REDUCED_MOTION]: { transition: 'none !important' },
});

const bandSx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...phoneGutter(theme),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  backgroundColor: SemanticError[600],
  color: theme.palette.common.white,
  paddingBlockStart: `max(env(safe-area-inset-top), ${theme.spacing(6)})`,
  paddingBlockEnd: theme.spacing(4),
  [theme.breakpoints.up('md')]: { paddingInline: theme.spacing(CARD_GUTTER_MD), paddingBlockStart: theme.spacing(5) },
});

const bandTitleSx: SystemStyleObject<Theme> = { display: 'flex', alignItems: 'center', gap: 3 };

const iconRingSxOf =
  (pulsing: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    inlineSize: theme.controlHeight.medium,
    blockSize: theme.controlHeight.medium,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.common.white}`,
    animation: pulsing ? `${pulse} ${ALARM_PULSE_MS}ms ${theme.transitions.easing.easeInOut} infinite` : 'none',
    [REDUCED_MOTION]: { animation: 'none' },
  });
const ICON_RING_SX = { pulsing: iconRingSxOf(true), still: iconRingSxOf(false) } as const;

const bandLabelSx = (theme: Theme): SystemStyleObject<Theme> => ({
  color: theme.palette.common.white,
  '&:lang(hi)': { letterSpacing: 0 },
});

const statusSx: SystemStyleObject<Theme> = { color: 'inherit' };
const statusLineSx: SystemStyleObject<Theme> = { display: 'block' };

const bodySx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...phoneGutter(theme),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  minBlockSize: 0,
  overflowY: 'auto',
  paddingBlock: theme.spacing(5),
  [theme.breakpoints.up('md')]: { paddingInline: theme.spacing(CARD_GUTTER_MD) },
});

const titleSx = (theme: Theme): SystemStyleObject<Theme> => ({
  color: Neutrals[900],
  textWrap: 'balance',
  overflowWrap: 'anywhere',
  lineHeight: theme.typography.lineHeightHeading,
});
const detailSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: 3 };
const metaSx: SystemStyleObject<Theme> = { color: Neutrals[700], fontVariantNumeric: 'tabular-nums' };
const readingSx: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  columnGap: 2,
  margin: 0,
  color: Neutrals[900],
  fontVariantNumeric: 'tabular-nums',
};
const readingValueSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: theme.typography.title.fontSize,
  fontWeight: theme.typography.title.fontWeight,
  lineHeight: theme.typography.lineHeightTight,
});
const readingRestSx: SystemStyleObject<Theme> = { color: Neutrals[700] };
const moreSx: SystemStyleObject<Theme> = { color: SemanticError[900] };

const BigReading: React.FC<{ reading: IssueReading }> = ({ reading }) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const context = readingContext(t, reading);
  return (
    <Box component="p" sx={readingSx}>
      <Box component="span" sx={readingValueSx}>
        {formatReading(reading.value, locale)}
      </Box>
      <Typography component="span" variant="highlight" sx={readingRestSx}>
        {context === null ? reading.unit : `${reading.unit} ${context}`}
      </Typography>
    </Box>
  );
};

const AlarmStatus: React.FC<{ id: string; lines: readonly string[] }> = ({ id, lines }) => (
  <Typography id={id} role="status" component="p" variant="highlight" sx={lines.length === 0 ? visuallyHiddenSx : statusSx}>
    {lines.map((line) => (
      <Box component="span" key={line} sx={statusLineSx}>
        {line}
      </Box>
    ))}
  </Typography>
);

const startButtonOf = (t: TFunction, canStart: boolean, starting: boolean, onStart: () => void): React.ReactElement | undefined => {
  if (!canStart) return undefined;
  return (
    <Button intent="danger" busy={starting} onClick={onStart}>
      {t('closeTheLoop.inApp.startAndSilence', 'Start and silence the alarm')}
    </Button>
  );
};

const muteButtonOf = (t: TFunction, alarm: AlarmTone): React.ReactElement | undefined => {
  if (alarm.state === 'muted') {
    return (
      <Button intent="tertiary" aria-disabled onClick={alarm.mute}>
        {t('closeTheLoop.inApp.alarmMutedButton', 'Alarm muted')}
      </Button>
    );
  }
  if (alarm.state !== 'ringing' && alarm.state !== 'blocked') return undefined;
  return (
    <Button intent="tertiary" onClick={alarm.mute}>
      {t('closeTheLoop.inApp.muteAlarm', 'Mute alarm')}
    </Button>
  );
};

interface OverlayDockProps {
  readonly phase: AlertPhase;
  readonly canStart: boolean;
  readonly alarm: AlarmTone;
  readonly answerRef: React.RefObject<HTMLButtonElement>;
  readonly onStart: () => void;
  readonly onDismiss: () => void;
}

const OverlayDock: React.FC<OverlayDockProps> = ({ phase, canStart, alarm, answerRef, onStart, onDismiss }) => {
  const { t } = useTranslation();
  const hintId = useId();
  if (phase.kind === 'taken') {
    return (
      <StickyActionBar
        layout="dock"
        placement="surface"
        announce={false}
        sx={dockSx}
        primary={
          <Button ref={answerRef} intent="secondary" onClick={onDismiss}>
            {t('closeTheLoop.inApp.ok', 'OK')}
          </Button>
        }
      />
    );
  }
  return (
    <StickyActionBar
      layout="dock"
      placement="surface"
      announce={false}
      sx={dockSx}
      message={
        <Typography id={hintId} variant="caption2" component="p">
          {t('closeTheLoop.inApp.someoneElseHint', 'Only hides this on your screen. The alert stays open for your team.')}
        </Typography>
      }
      secondary={
        <Button ref={answerRef} intent="secondary" aria-describedby={hintId} onClick={onDismiss}>
          {t('closeTheLoop.inApp.someoneElse', 'Someone else is on it')}
        </Button>
      }
      primary={startButtonOf(t, canStart, phase.kind === 'starting', onStart)}
      tertiary={muteButtonOf(t, alarm)}
    />
  );
};

export interface EmergencyOverlayProps {
  readonly item: FeedItem;
  readonly now: number;
  readonly phase: AlertPhase;
  readonly plantName: string | null;
  readonly moreCount: number;
  readonly canStart: boolean;
  readonly startFailed: boolean;
  readonly offline: boolean;
  readonly onStart: () => void;
  readonly onDismiss: () => void;
  readonly onShown: () => void;
  readonly onExited: () => void;
}

export const EmergencyOverlay: React.FC<EmergencyOverlayProps> = ({
  item,
  now,
  phase,
  plantName,
  moreCount,
  canStart,
  startFailed,
  offline,
  onStart,
  onDismiss,
  onShown,
  onExited,
}) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const eyebrowId = useId();
  const titleId = useId();
  const statusId = useId();
  const detailId = useId();
  const answerRef = useRef<HTMLButtonElement>(null);
  const open = phase.kind !== 'closing';
  const ringing = open && phase.kind === 'live';
  const alarm = useAlarmTone(ringing);
  const lines = alertLines(t, item, locale);
  const status = alarmStatusLines(t, { phase, hooter: lines.hooter, offline, alarm: alarm.state });

  const onClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'escapeKeyDown') answerRef.current?.focus();
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition sx={rootSx} slotProps={{ backdrop: { sx: backdropSx } }}>
      <Fade in={open} onEnter={onShown} onExited={onExited}>
        <Box
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={`${eyebrowId} ${titleId}`}
          aria-describedby={`${statusId} ${detailId}`}
          tabIndex={-1}
          sx={cardSx}
        >
          <Box sx={bandSx}>
            <Box sx={bandTitleSx}>
              <Box aria-hidden sx={ringing ? ICON_RING_SX.pulsing : ICON_RING_SX.still}>
                <ReportProblemOutlinedIcon />
              </Box>
              <Typography id={eyebrowId} variant="sectionLabel" component="p" sx={bandLabelSx}>
                {emergencyEyebrow(t, plantName)}
              </Typography>
            </Box>
            <AlarmStatus id={statusId} lines={status} />
          </Box>
          <Box sx={bodySx}>
            <Typography id={titleId} variant="h1" component="h2" sx={titleSx}>
              {lines.title}
            </Typography>
            <Box id={detailId} sx={detailSx}>
              <Typography variant="label" component="p" sx={metaSx}>
                {firedLine(t, item.location, firedWhen(t, item.at, now))}
              </Typography>
              {item.reading !== null && <BigReading reading={item.reading} />}
              {moreCount > 0 && (
                <Typography variant="highlight" component="p" sx={moreSx}>
                  {moreEmergenciesText(t, moreCount)}
                </Typography>
              )}
            </Box>
            {startFailed && <InlineAlert severity="error" role="alert" message={startFailedText(t)} />}
          </Box>
          <OverlayDock phase={phase} canStart={canStart} alarm={alarm} answerRef={answerRef} onStart={onStart} onDismiss={onDismiss} />
        </Box>
      </Fade>
    </Modal>
  );
};
